import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useViewport } from "@primitives/index";
import { QueueList, QueueWorkItems, isWorkItemLockedAt } from "@compositions/index";
import type { DestinationQueue, WorkflowViewMode, WorkflowSearchResult } from "@compositions/index";
import { WorkflowScene } from "./WorkflowScene";
import { useGalileoNavigation, galileoEntityHref } from "./navigation";
import { useAuth } from "./auth/useAuth";
import { buildWorkflowSearch, useSearchWorkflows } from "./workflows";
import type { WorkItem } from "./workflows";
import { useMyQueues, useQueueTree, useQueueWorkItemTallies } from "./queues";
import type { QueueSummary } from "./queues";
import { useWorkItemsByQueue } from "./workitems";
import type { CompletedWorkItemPageSize, QueueWorkItemView } from "./workitems";
import { useConnectionState } from "./realtime";
import { WorkItemRowPresence } from "./realtime/presence";
import { usePushRegistration, useQueueNotificationPreferences } from "./push";
import { ShellFrame } from "./workflow-layout/ShellFrame";
import { WorkflowTopBar, type WorkflowTopBarHandle } from "./workflow-layout/WorkflowTopBar";
import { WorkspaceHome } from "./workflow-layout/WorkspaceHome";
import { useNextDispenser } from "./workflow-layout/useNextDispenser";
import { useWorkflowDialogs, type WorkflowTarget } from "./workflow-layout/useWorkflowDialogs";
import { WorkflowOverlays } from "./workflow-layout/WorkflowOverlays";
import { useGlobalShortcuts } from "./shortcuts";
import styles from "./workflow-layout/SceneStack.module.css";

/** Both scenes keep `.scene` (the grid cell); hidden adds the fade-out modifier. */
function sceneClass(active: boolean, ...extra: readonly (string | undefined)[]): string {
  return [styles.scene, active ? undefined : styles.sceneHidden, ...extra]
    .filter((name): name is string => name !== undefined)
    .join(" ");
}

interface WorkflowRef {
  readonly id: string;
  readonly entityType: string;
  readonly entityId: string;
}

export function WorkflowLayout(): ReactElement {
  const auth = useAuth();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  // On compact, `WorkspaceHome` names the queue in its sub-header.
  const { isCompact } = useViewport();

  // The queue-cache reconciliation hook (`useQueueRealtime`) is mounted once
  // for the whole authenticated app in ExperienceShell. This layout only
  // reads the connection state for the indicator.
  const realtimeState = useConnectionState();

  // Web Push availability for this environment. The device toggle itself
  // lives on the Notifications settings page; here `available` only gates
  // the per-queue mute affordances on the rail.
  const push = usePushRegistration();
  const queueMutes = useQueueNotificationPreferences(push.available);

  const { navigateToGalileo } = useGalileoNavigation();

  const handleNavigateToGalileoEntity = useCallback(
    (entityType: string, entityId: string) => {
      const href = galileoEntityHref(entityType, entityId);
      if (href !== null) {
        navigateToGalileo(href);
      }
    },
    [navigateToGalileo],
  );

  const handleOpenEntityFromBoard = useCallback(
    (entityType: string, entityId: string) => {
      void navigate(`/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`);
    },
    [navigate],
  );

  // The active workflow is hydrated from the URL so deep links and refreshes
  // load it directly. The backend needs entityType + entityId for the
  // partition lookup, so they travel as query params beside the id.
  const activeWorkflow: WorkflowRef | null = useMemo(() => {
    if (params.id === undefined || params.id.length === 0) return null;
    const entityType = searchParams.get("entityType") ?? "";
    const entityId = searchParams.get("entityId") ?? "";
    if (entityType.length === 0 || entityId.length === 0) return null;
    return { id: params.id, entityType, entityId };
  }, [params.id, searchParams]);

  // The workflow scene unmounts the moment the URL drops the id. It is not
  // kept for a fade-out: its board reads its selection from the URL, so a
  // lingering copy would repaint (and could write) against the workspace
  // URL. The workspace fades in over the canvas instead.
  const workflowActive = activeWorkflow !== null;

  // The queue filter is URL-driven so deep links, refreshes, and the back
  // button behave naturally.
  const selectedQueueId = useMemo(() => {
    const value = searchParams.get("queueId") ?? "";
    return value.length > 0 ? value : null;
  }, [searchParams]);

  // Detail view selection is URL-driven. "board" is the default; any
  // unrecognized value degrades to the board.
  const workflowView = useMemo<WorkflowViewMode>(() => {
    const raw = searchParams.get("view");
    if (raw === "timeline") return "timeline";
    return "board";
  }, [searchParams]);

  const handleViewChange = useCallback(
    (next: WorkflowViewMode) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "board") {
            params.delete("view");
          } else {
            params.set("view", next);
          }
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const [searchQuery, setSearchQuery] = useState("");
  const { data: searchData, isFetching: isSearching } = useSearchWorkflows(searchQuery);
  const searchResults: readonly WorkflowSearchResult[] = searchData?.items ?? [];
  const searchTotalCount = searchData?.totalCount;

  const { data: queuesData, isLoading: isLoadingQueues, error: queuesError } = useMyQueues();
  // The @-mention picker addresses any queue in the org, so the full tree
  // is hydrated on workspace mount and cached by react-query.
  const { data: queueTreeData } = useQueueTree();

  // Operational queue ids the user belongs to. Drives approval visibility;
  // the server stays authoritative for mutations.
  const memberQueueIds = useMemo(
    () => new Set(queuesData?.items.map((q) => q.id) ?? []),
    [queuesData?.items],
  );
  const queueItems = useMemo(() => queuesData?.items ?? [], [queuesData?.items]);

  const destinationQueues = useMemo<readonly DestinationQueue[]>(
    () =>
      queueItems
        .filter((q) => q.isWorkable && q.status !== "deactivated")
        .map((q) => ({ id: q.id, name: q.name, isPersonal: q.isPersonal })),
    [queueItems],
  );

  // The asker's personal queue is the fallback originating queue for every
  // spawn affordance when no shared queue is selected.
  const personalQueueId = useMemo(() => {
    const personal = queueItems.find((q) => q.isPersonal);
    return personal?.id;
  }, [queueItems]);

  const queueIds = useMemo(() => queueItems.map((q) => q.id), [queueItems]);
  const { tallies: queueTallies, isLoading: isLoadingQueueTallies } =
    useQueueWorkItemTallies(queueIds);

  const selectedQueue: QueueSummary | null = useMemo(() => {
    if (selectedQueueId === null) return null;
    return queueItems.find((q) => q.id === selectedQueueId) ?? null;
  }, [selectedQueueId, queueItems]);

  const [queueWorkItemView, setQueueWorkItemView] = useState<QueueWorkItemView>("active");
  const [completedWorkItemsSkip, setCompletedWorkItemsSkip] = useState(0);
  const [completedWorkItemsTake, setCompletedWorkItemsTake] =
    useState<CompletedWorkItemPageSize>(20);
  const queueWorkItemsQuery = useWorkItemsByQueue(
    queueWorkItemView === "completed"
      ? {
          queueId: selectedQueueId ?? "",
          view: "completed",
          skip: completedWorkItemsSkip,
          take: completedWorkItemsTake,
        }
      : {
          queueId: selectedQueueId ?? "",
          view: "active",
        },
  );
  const queueWorkItems = queueWorkItemsQuery.data?.items ?? [];
  const queueWorkItemsTotal = queueWorkItemsQuery.data?.totalCount ?? 0;
  useEffect(() => {
    setCompletedWorkItemsSkip(0);
  }, [selectedQueueId]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const navigateToWorkflow = useCallback(
    (item: WorkflowTarget) => {
      const search = buildWorkflowSearch(
        item.entityType,
        item.entityId,
        selectedQueueId,
        item.compose !== undefined ? { compose: item.compose } : undefined,
      );
      void navigate(`/workflows/${item.id}?${search}`);
    },
    [navigate, selectedQueueId],
  );

  // Drill from a queue-focus row into its workflow. The queueId carried
  // forward is the selected queue, so the user keeps that context.
  const handleOpenWorkflowFromQueue = useCallback(
    (item: { id: string; workflowId: string; entityType: string; entityId: string }) => {
      if (item.entityType.length === 0 || item.entityId.length === 0) return;
      const search = buildWorkflowSearch(item.entityType, item.entityId, selectedQueueId, {
        itemId: item.id,
      });
      void navigate(`/workflows/${item.workflowId}?${search}`);
    },
    [navigate, selectedQueueId],
  );

  const handleSelect = useCallback(
    (result: WorkflowSearchResult) => {
      navigateToWorkflow(result);
    },
    [navigateToWorkflow],
  );

  const next = useNextDispenser(selectedQueueId);
  const dialogs = useWorkflowDialogs(navigateToWorkflow, selectedQueueId);

  const topBarRef = useRef<WorkflowTopBarHandle>(null);
  const focusSearch = useCallback(() => topBarRef.current?.focusSearch(), []);
  useGlobalShortcuts(
    useMemo(
      () => ({
        onNext: next.handleNext,
        onAsk: dialogs.ask.open,
        onCreate: dialogs.create.open,
        onFocusSearch: focusSearch,
      }),
      [next.handleNext, dialogs.ask.open, dialogs.create.open, focusSearch],
    ),
  );

  const handleBackToRecent = useCallback(() => {
    // Keep the queue filter so the user returns to the same focus context.
    const target =
      selectedQueueId !== null
        ? `/workflows?queueId=${encodeURIComponent(selectedQueueId)}`
        : "/workflows";
    void navigate(target);
  }, [navigate, selectedQueueId]);

  // Selecting a queue is a filter, not navigation. Selecting the active
  // queue again clears the filter.
  const handleQueueSelect = useCallback(
    (queue: QueueSummary) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (next.get("queueId") === queue.id) {
            next.delete("queueId");
          } else {
            next.set("queueId", queue.id);
          }
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const handleClearQueueSelection = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("queueId");
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const handleToggleIncludeCompleted = useCallback(() => {
    setCompletedWorkItemsSkip(0);
    setQueueWorkItemView((current) => (current === "active" ? "completed" : "active"));
  }, []);

  const handleSignOut = useCallback(() => {
    void auth.signOut();
  }, [auth]);

  // The admin area opens on /queues; AdminLayout's nav bar takes over from
  // there.
  const handleOpenAdmin = useCallback(() => {
    void navigate("/queues");
  }, [navigate]);

  const handleOpenSettings = useCallback(() => {
    void navigate("/settings");
  }, [navigate]);

  // The asker's personal queue is excluded from the Ask picker. The server
  // adds it to the distribution automatically.
  const excludeQueueIdsForAsk = useMemo<readonly string[]>(
    () => (personalQueueId !== undefined ? [personalQueueId] : []),
    [personalQueueId],
  );

  // The queue this Ask is attributed to, for display only. The submit path
  // omits the field when no shared queue is selected.
  const effectiveAskOriginatingQueueId = useMemo<string | undefined>(
    () => selectedQueueId ?? personalQueueId,
    [selectedQueueId, personalQueueId],
  );

  const currentUserId = auth.account?.localAccountId;

  // Stable across renders so memoized work item rows skip the 30s tick
  // unless their own labels change.
  const renderWorkItemPresence = useCallback((item: WorkItem): ReactElement => {
    const lockedId = isWorkItemLockedAt(item, new Date()) ? item.lockedBy : null;
    return (
      <WorkItemRowPresence
        workItemId={item.id}
        {...(lockedId !== null ? { lockedGalileoUserId: lockedId } : {})}
      />
    );
  }, []);

  return (
    <ShellFrame
      inlineGutter={false}
      topBar={
        <WorkflowTopBar
          ref={topBarRef}
          search={{
            onSearch: handleSearch,
            onSelect: handleSelect,
            results: searchResults,
            isLoading: isSearching,
            ...(searchTotalCount !== undefined ? { totalCount: searchTotalCount } : {}),
          }}
          realtimeState={realtimeState}
          account={{
            userName: auth.account?.name ?? "User",
            userEmail: auth.account?.username ?? "",
            onOpenSettings: handleOpenSettings,
            onOpenAdmin: handleOpenAdmin,
            onSignOut: handleSignOut,
          }}
          onAsk={dialogs.ask.open}
          onCreate={dialogs.create.open}
        />
      }
    >
      {/*
       * The scene boundary is workspace <-> workflow. Both scenes share
       * one grid cell and crossfade; the workspace stays mounted while
       * hidden so its list scroll and data survive a round trip. A
       * switch between two workflows keeps the workflow subtree
       * mounted; the board swaps data with placeholder dimming and
       * resets per-workflow state on its own inner boundary.
       */}
      <div className={styles.stack} data-scene-stack="">
        <div
          data-testid="scene-workspace"
          className={sceneClass(!workflowActive)}
          aria-hidden={workflowActive ? true : undefined}
        >
          <WorkspaceHome
            selectedQueueId={selectedQueueId}
            selectedQueueName={selectedQueue?.name ?? null}
            onClearSelection={handleClearQueueSelection}
            rail={
              <QueueList
                items={queueItems}
                isLoading={isLoadingQueues}
                error={queuesError}
                onSelect={handleQueueSelect}
                selectedQueueId={selectedQueueId}
                tallies={queueTallies}
                talliesLoading={isLoadingQueueTallies}
                onManage={(queue) => {
                  void navigate(`/queues/${queue.id}`);
                }}
                {...(push.available
                  ? {
                      mutedQueueIds: queueMutes.mutedQueueIds,
                      onToggleMute: (queue: QueueSummary) => {
                        queueMutes.toggleMute(queue.id);
                      },
                    }
                  : {})}
              />
            }
            focus={
              selectedQueue !== null ? (
                <QueueWorkItems
                  queueName={selectedQueue.name}
                  hideHeading={isCompact}
                  items={queueWorkItems}
                  totalCount={queueWorkItemsTotal}
                  isLoading={queueWorkItemsQuery.isLoading}
                  isStale={queueWorkItemsQuery.isPlaceholderData}
                  error={queueWorkItemsQuery.error}
                  view={queueWorkItemView}
                  onToggleCompleted={handleToggleIncludeCompleted}
                  skip={completedWorkItemsSkip}
                  take={completedWorkItemsTake}
                  onPageChange={setCompletedWorkItemsSkip}
                  onPageSizeChange={(take) => {
                    setCompletedWorkItemsTake(take);
                    setCompletedWorkItemsSkip(0);
                  }}
                  onOpenWorkflow={handleOpenWorkflowFromQueue}
                  onNavigateToGalileoEntity={handleNavigateToGalileoEntity}
                  renderPresence={renderWorkItemPresence}
                  onNext={next.handleNext}
                  nextPending={next.isPending}
                />
              ) : null
            }
          />
        </div>
        {activeWorkflow !== null && (
          <div
            data-testid="scene-workflow"
            className={sceneClass(true, styles.workflow, styles.sceneEnter)}
          >
            <WorkflowScene
              id={activeWorkflow.id}
              entityType={activeWorkflow.entityType}
              entityId={activeWorkflow.entityId}
              currentUserId={currentUserId}
              originatingQueueId={selectedQueueId ?? personalQueueId}
              queueTree={queueTreeData}
              memberQueueIds={memberQueueIds}
              destinationQueues={destinationQueues}
              onBack={handleBackToRecent}
              view={workflowView}
              onViewChange={handleViewChange}
              onOpenEntity={() =>
                handleOpenEntityFromBoard(activeWorkflow.entityType, activeWorkflow.entityId)
              }
              onNavigateToGalileoEntity={() =>
                handleNavigateToGalileoEntity(activeWorkflow.entityType, activeWorkflow.entityId)
              }
              onNext={next.handleNext}
              nextPending={next.isPending}
            />
          </div>
        )}
      </div>

      {/*
       * Fixed-to-viewport layers live outside the scene stack, so a
       * scene switch never remounts them and no scene ancestor can
       * capture their `position: fixed`.
       */}
      <WorkflowOverlays
        next={next}
        dialogs={dialogs}
        queueTree={queueTreeData}
        sendAsQueues={queuesData?.items}
        excludeQueueIdsForAsk={excludeQueueIdsForAsk}
        askOriginatingQueueId={effectiveAskOriginatingQueueId}
      />
    </ShellFrame>
  );
}
